import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { Plus, Check, Trash2, Clock, AlertCircle } from 'lucide-react';
import './Notes.css';

type Note = {
    id: number;
    content: string;
    is_done: boolean;
    created_at: string;
};

type NotesProps = {
    onNotesChange: () => void; // Callback to trigger re-fetch of count in parent
};

export function Notes({ onNotesChange }: NotesProps) {
    const [notes, setNotes] = useState<Note[]>([]);
    const [newNote, setNewNote] = useState('');
    const [loading, setLoading] = useState(true);
    const [showDone, setShowDone] = useState(false);

    useEffect(() => {
        fetchNotes();
    }, []);

    const fetchNotes = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('notes')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setNotes(data || []);
        } catch (error) {
            console.error('Error fetching notes:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddNote = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newNote.trim()) return;

        try {
            const { error } = await supabase
                .from('notes')
                .insert([{ content: newNote, is_done: false }]);

            if (error) throw error;

            setNewNote('');
            fetchNotes();
            onNotesChange(); // Update badge in parent
        } catch (error) {
            console.error('Error adding note:', error);
        }
    };

    const toggleNoteStatus = async (note: Note) => {
        try {
            // Optimistic update
            const updatedNotes = notes.map(n =>
                n.id === note.id ? { ...n, is_done: !n.is_done } : n
            );
            setNotes(updatedNotes);

            const { error } = await supabase
                .from('notes')
                .update({ is_done: !note.is_done })
                .eq('id', note.id);

            if (error) {
                throw error;
                // Revert if needed? Simple app, maybe just log
            }
            onNotesChange();
        } catch (error) {
            console.error('Error updating note:', error);
            fetchNotes(); // Revert on error
        }
    };

    const deleteNote = async (id: number) => {
        if (!window.confirm('¿Borrar esta nota?')) return;

        try {
            setNotes(notes.filter(n => n.id !== id));

            const { error } = await supabase
                .from('notes')
                .delete()
                .eq('id', id);

            if (error) throw error;
            onNotesChange();
        } catch (error) {
            console.error('Error deleting note:', error);
            fetchNotes();
        }
    };

    const pendingNotes = notes.filter(n => !n.is_done);
    const doneNotes = notes.filter(n => n.is_done);

    // If showDone is false, we only show pending. 
    // If showDone is true, maybe we show all or just done? 
    // Usually lists show Pending, and "Show Completed" reveals them securely.

    return (
        <div className="notes-container">
            <div className="notes-input-section">
                <form onSubmit={handleAddNote} className="note-form">
                    <textarea
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        placeholder="Escribe una nueva nota..."
                        rows={3}
                        autoFocus
                    />
                    <button type="submit" className="add-note-btn" disabled={!newNote.trim()}>
                        <Plus size={18} /> Agregar
                    </button>
                </form>
            </div>

            <div className="notes-list-section">
                {loading && notes.length === 0 ? (
                    <div className="loading-spinner">Cargando notas...</div>
                ) : (
                    <>
                        <div className="notes-header">
                            <h3>Pendientes ({pendingNotes.length})</h3>
                        </div>

                        {pendingNotes.length === 0 && (
                            <div className="empty-notes">
                                <Check size={48} className="text-green-500" />
                                <p>¡Todo al día! No hay notas pendientes.</p>
                            </div>
                        )}

                        <div className="notes-grid">
                            {pendingNotes.map(note => (
                                <div key={note.id} className="note-card pending">
                                    <div className="note-content">{note.content}</div>
                                    <div className="note-meta">
                                        <span><Clock size={12} /> {new Date(note.created_at).toLocaleString()}</span>
                                    </div>
                                    <div className="note-actions">
                                        <button
                                            className="action-btn done-btn"
                                            onClick={() => toggleNoteStatus(note)}
                                            title="Marcar como listo"
                                        >
                                            <Check size={16} /> Listo
                                        </button>
                                        <button
                                            className="action-btn delete-btn"
                                            onClick={() => deleteNote(note.id)}
                                            title="Eliminar"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="notes-divider">
                            <button
                                className="toggle-done-btn"
                                onClick={() => setShowDone(!showDone)}
                            >
                                {showDone ? 'Ocultar completadas' : `Ver completadas (${doneNotes.length})`}
                            </button>
                        </div>

                        {showDone && (
                            <div className="notes-grid done-grid">
                                {doneNotes.map(note => (
                                    <div key={note.id} className="note-card done">
                                        <div className="note-content">{note.content}</div>
                                        <div className="note-meta">
                                            <span><Clock size={12} /> {new Date(note.created_at).toLocaleString()}</span>
                                        </div>
                                        <div className="note-actions">
                                            <button
                                                className="action-btn undo-btn"
                                                onClick={() => toggleNoteStatus(note)}
                                                title="Desmarcar"
                                            >
                                                Deshacer
                                            </button>
                                            <button
                                                className="action-btn delete-btn"
                                                onClick={() => deleteNote(note.id)}
                                                title="Eliminar"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
